import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { finalize, firstValueFrom } from 'rxjs';
import { ContentTypeLabelPipe } from '../../../../../shared/pipes/content-type-label.pipe';
import {
  BreadcrumbComponent,
  BreadcrumbLevel,
} from '../../../../../shared/components/breadcrumb/breadcrumb.component';
import { SpinnerComponent } from '../../../../../shared/components/az-spinner/spinner.component';
import {
  isJobStatusCancellable,
  JOB_STATUSES_NEW_TAGS,
  LANGUAGES,
  QUALIFYING_STATUSES_FOR_REVISED_DTP,
  REJECTED_REQUEST_SUPPORT_EMAIL,
  REJECTED_REQUEST_SUPPORT_SUBJECT,
  TRANSLATED_FILES_ENABLED_STATUSES,
} from '../../../../../shared/config/constants';
import { S3FilesService } from '../../../../../shared/services/s3files.service';
import {
  S3DestinationFolder,
  TranslationFileType,
} from '../../../../../shared/models/s3-destination-folder';
import {
  VendorOrder,
  VendorOrderFile,
} from '../../../../../shared/models/vendor-order.model';
import { VendorOrdersService } from '../services/vendor-orders.service';
import { VendorService } from '../../../../../shared/services/vendor.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { PopupService } from '../../../../../shared/components/popup/popup.service';
import { QuotesComponent } from '../quotes/quotes.component';
import { TranslatedFilesComponent } from '../translated-files/translated-files.component';
import { MessagesComponent } from '../../../../../features/messages/messages.component';
import {
  MESSAGES_LEGACY_ENABLED_STATUSES,
  MESSAGES_LEGACY_READONLY_STATUSES,
} from '../../../../../features/messages/messages.constants';
import {
  MessagePolicyEvaluation,
  computeMessagePolicy,
} from '../../../../../features/messages/message-retention-policy.util';
import { ToolbarTitleService } from '../../../components/toolbar/toolbar-title.service';
import {
  CANCELLATION_REASON_DISPLAY_MAP,
  CancelFlowState,
  CancelReasonDialogResult,
} from './cancel-job/cancel-job.types';
import { CancelJobScopeDialogComponent } from './cancel-job/cancel-job-scope-dialog.component';
import { CancelJobReasonDialogComponent } from './cancel-job/cancel-job-reason-dialog.component';
import { CancelJobSuccessDialogComponent } from './cancel-job/cancel-job-success-dialog.component';
import { CancelJobSuccessContentComponent } from './cancel-job-success-content.component';
import { CancelJobConfirmContentComponent } from './cancel-job-confirm-content.component';

/** Represents a source file with its translation type classification. */
interface SourceFile {
  name: string;
  size: string;
  bytes: number;
}

type FileSortColumn = 'name' | 'size';

interface FileSortState {
  column: FileSortColumn;
  descending: boolean;
}

type FileTableType = 'basic' | 'dtp' | 'reference';
type JobDetailTabId = 'details' | 'quotes' | 'translated-files' | 'messages';

interface JobDetailTab {
  id: JobDetailTabId;
  label: string;
}

/** View model for the job detail page. */
interface JobDetailView {
  jobUid: string;
  jobNumber: string;
  jobName: string;
  requestDate: string;
  targetDate: string;
  statusLabel: string;
  statusKey: string;
  statusIcon: string | null;
  contentType: string;
  therapeuticArea: string;
  costCentre: string;
  targetAudience: string;
  translationCertificate: string;
  documentTone: string;
  additionalInfo: string;
  cancellationReason: string;
  cancellationDate?: string;
  sourceLanguage: string;
  targetLanguages: string[];
  basicFormatFiles: SourceFile[];
  dtpFiles: SourceFile[];
  referenceFiles: SourceFile[];
}

const LOCALE_LOOKUP = new Map<string, string>(Object.entries(LANGUAGES));

const STATUS_ICON_MAP: Readonly<Record<string, string>> = {
  'Quotes Pending': 'schedule',
  'Quotes to be Reviewed': 'request_quote',
  'In Progress': 'article_person',
  Cancelled: 'cancel',
  Completed: 'check_circle',
  Rejected: 'do_not_disturb_on',
};

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatIconModule,
    SpinnerComponent,
    ContentTypeLabelPipe,
    QuotesComponent,
    TranslatedFilesComponent,
    MessagesComponent,
    SpinnerComponent,
  ],
  templateUrl: './job-detail.component.html',
  styleUrl: './job-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly vendorService = inject(VendorService);
  private readonly toastService = inject(ToastService);
  private readonly s3FilesService = inject(S3FilesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toolbarTitleService = inject(ToolbarTitleService);
  private readonly matDialog = inject(MatDialog);
  private readonly popupService = inject(PopupService);
  readonly notificationService = inject(NotificationService);
  private readonly order = signal<VendorOrder | null>(null);
  private readonly hasWindow = typeof window !== 'undefined';
  private readonly selectedFilters = signal<string[]>([]);
  private readonly notificationSnapshotOnEntry = signal('');
  private readonly sourceFiles = signal<SourceFile[]>([]);
  private readonly referenceFiles = signal<SourceFile[]>([]);
  readonly activeTab = signal<JobDetailTabId>('details');
  readonly renderedTab = signal<JobDetailTabId>('details');
  readonly tabContentState = signal<'idle' | 'exiting' | 'entering'>('idle');
  readonly tabIndicatorWidth = signal(0);
  readonly tabIndicatorOffset = signal(0);
  readonly tabIndicatorTransform = computed(
    () => `translateX(${this.tabIndicatorOffset()}px)`,
  );
  private readonly vendorName: string | undefined = (
    history.state as { vendorName?: string } | null
  )?.vendorName;
  private readonly rawJobStatus: string | undefined = (
    history.state as { jobStatus?: string } | null
  )?.jobStatus;
  readonly hasNewMessage = signal<boolean>(
    (history.state as { newMessage?: boolean } | null)?.newMessage === true,
  );
  readonly basicFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly dtpFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly referenceFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly isLoading = signal(true);
  readonly isFilesLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly cancelFlowState = signal<CancelFlowState>({
    scope: null,
    reason: '',
    isSubmitting: false,
  });
  private static readonly TAB_FADE_DURATION_MS = 180;

  private fadeExitTimerId: ReturnType<typeof setTimeout> | null = null;
  private fadeEnterTimerId: ReturnType<typeof setTimeout> | null = null;

  readonly tabs = computed<readonly JobDetailTab[]>(() => {
    const baseTabs: JobDetailTab[] = [
      { id: 'details', label: 'Job Details' },
      { id: 'quotes', label: 'Quotes' },
    ];

    if (this.isInProgressOrLater()) {
      baseTabs.push({ id: 'messages', label: 'Messages' });
    }

    if (this.isTranslatedFilesTabEnabled()) {
      baseTabs.push({ id: 'translated-files', label: 'Translated Files' });
    }

    return baseTabs;
  });

  readonly isTranslatedFilesTabEnabled = computed(() => {
    const order = this.order();
    const status = order?.jobStatus;
    if (!status) return false;
    const normalized = status.toUpperCase().replaceAll(/[\s-]/g, '_');

    // Hide when all quotes expired — no files were ever translated
    if (order?.allQuotesExpired === true) return false;

    // For CANCELLED status, only show if purchaseOrderId exists
    if (normalized === 'CANCELLED') {
      const po = order?.purchaseOrderId;
      return typeof po === 'string' ? po.trim().length > 0 : Boolean(po);
    }

    // For other statuses, check against enabled statuses list
    // (REJECTED and other statuses not in the list will return false)
    return (TRANSLATED_FILES_ENABLED_STATUSES as readonly string[]).includes(
      normalized,
    );
  });

  readonly isInProgressJob = computed(() => {
    const status = this.order()?.jobStatus;
    return status === 'IN_PROGRESS' || status === 'AWAITING_DTP_PROCESS';
  });

  readonly isCancelledJob = computed(() => {
    return this.order()?.jobStatus === 'CANCELLED';
  });

  readonly canShowCancelAction = computed(() => {
    if (this.isCancelledJob()) {
      return false;
    }

    return this.isInProgressJob() || this.canCancelJob();
  });

  onCancelJobClick(): void {
    if (this.isCancelledJob()) {
      return;
    }

    if (this.isInProgressJob()) {
      this.openCancelDialog();
      return;
    }

    this.openLegacyCancelDialog();
  }

  private openLegacyCancelDialog(): void {
    const jobUid = this.order()?.jobUid;
    if (!jobUid || !this.canCancelJob()) {
      return;
    }

    this.popupService.open<boolean>({
      title: 'Cancel Job',
      width: '622px',
      maxWidth: '96vw',
      component: CancelJobConfirmContentComponent,
      componentInputs: {
        confirmAction: () => this.cancelJob(jobUid),
      },
      hideButtons: true,
      dialogTestId: 'cancel-job-confirmation-dialog',
    });
  }

  private openCancelDialog(): void {
    const dialogRef = this.matDialog.open<CancelJobScopeDialogComponent>(
      CancelJobScopeDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.continueClicked.subscribe(
      (result: { scope: 'ENTIRE_JOB' | 'SPECIFIC_FILES' }) => {
        if (result.scope === 'ENTIRE_JOB') {
          this.cancelFlowState.update((state) => ({
            ...state,
            scope: result.scope,
          }));
          this.openCancelReasonDialog();
        } else {
          this.resetCancelFlowState();
        }
        dialogRef.close();
      },
    );

    dialogRef.componentInstance.closeClicked.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private openCancelReasonDialog(): void {
    const dialogRef = this.matDialog.open<CancelJobReasonDialogComponent>(
      CancelJobReasonDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.confirmed.subscribe(
      (result: CancelReasonDialogResult) => {
        const reason = result.reason?.trim() ?? '';
        if (!result.confirmed || reason.length === 0) {
          return;
        }

        this.submitCancelEntireJob(reason);
        dialogRef.close();
      },
    );

    dialogRef.componentInstance.closed.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private submitCancelEntireJob(reason: string): void {
    const jobUid = this.order()?.jobUid;
    if (!jobUid) {
      return;
    }

    this.cancelFlowState.update((state) => ({
      ...state,
      reason,
      isSubmitting: true,
    }));

    this.vendorService
      .cancelVendorOrder(jobUid, reason)
      .pipe(
        finalize(() => {
          this.cancelFlowState.update((state) => ({
            ...state,
            isSubmitting: false,
          }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.cancelFlowState.update((state) => ({
            ...state,
            reason,
          }));
          this.openCancelSuccessDialog();
          this.loadOrder(jobUid, true);
        },
        error: () => {
          this.toastService.showAtElement(
            this.hostEl.nativeElement,
            'Unable to cancel this job right now. Please try again.',
            { type: 'error' },
          );
        },
      });
  }

  private openCancelSuccessDialog(): void {
    const dialogRef = this.matDialog.open<CancelJobSuccessDialogComponent>(
      CancelJobSuccessDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        maxWidth: '622px',
        height: '279px',
        minHeight: '279px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.closeClicked.subscribe(() => {
      dialogRef.close();
    });
  }

  private resetCancelFlowState(): void {
    this.cancelFlowState.set({
      scope: null,
      reason: '',
      isSubmitting: false,
    });
  }

  readonly messagesPolicy = computed<MessagePolicyEvaluation>(() => {
    const order = this.order();
    if (!order) {
      return {
        state: 'HIDDEN',
        anchorEpochMs: null,
        cutoffEpochMs: null,
        reason: 'Missing order details',
      };
    }

    return computeMessagePolicy({
      jobStatus: order.jobStatus,
      purchaseOrderId: order.purchaseOrderId,
      cancellationDate: order.cancellationDate,
      translationCompleteDate: order.translationCompleteDate,
      nowEpochMs: Date.now(),
    });
  });

  readonly isInProgressOrLater = computed(() => {
    const policyState = this.messagesPolicy().state;
    if (policyState === 'INTERACTIVE' || policyState === 'READ_ONLY') {
      return true;
    }

    const status = this.order()?.jobStatus;
    if (!status) return false;
    const normalized = status.toUpperCase().replaceAll(/[\s-]/g, '_');
    return (MESSAGES_LEGACY_ENABLED_STATUSES as readonly string[]).includes(
      normalized,
    );
  });

  readonly isMessagesReadOnly = computed(() => {
    const policyState = this.messagesPolicy().state;
    if (policyState === 'READ_ONLY') {
      return true;
    }
    if (policyState === 'INTERACTIVE') {
      return false;
    }

    const status = this.order()?.jobStatus;
    if (!status) return true;
    const normalized = status.toUpperCase().replaceAll(/[\s-]/g, '_');
    return (MESSAGES_LEGACY_READONLY_STATUSES as readonly string[]).includes(
      normalized,
    );
  });

  readonly tabsNav = viewChild<ElementRef<HTMLElement>>('tabsNav');
  readonly tabButtons =
    viewChildren<ElementRef<HTMLButtonElement>>('tabButton');
  readonly breadcrumbLevels = computed<BreadcrumbLevel[]>(() => {
    const detail = this.jobDetail();
    const levels: BreadcrumbLevel[] = [
      {
        label: 'Translation Manager',
        route: '/translation-manager',
        state: {
          fromBreadcrumb: true,
          selectedFilters: this.selectedFilters(),
          notificationSnapshot: this.notificationSnapshotOnEntry(),
        },
      },
    ];

    if (detail?.jobUid) {
      levels.push({ label: detail.jobUid });
    }

    return levels;
  });

  readonly jobDetail = computed<JobDetailView | null>(() => {
    const order = this.order();
    if (!order) {
      return null;
    }
    return this.mapOrderToView(order);
  });
  readonly fullOrder = computed(() => this.order());
  readonly canCancelJob = computed(() =>
    isJobStatusCancellable(this.order()?.jobStatus),
  );
  readonly isRejectedRequest = computed(
    () => this.order()?.jobStatus === 'REJECTED',
  );
  readonly rejectedSupportHref = computed(
    () =>
      `mailto:${REJECTED_REQUEST_SUPPORT_EMAIL}?subject=${encodeURIComponent(REJECTED_REQUEST_SUPPORT_SUBJECT)}`,
  );

  ngOnInit(): void {
    this.selectedFilters.set(this.getSelectedFiltersFromState());
    this.notificationSnapshotOnEntry.set(
      this.notificationService.notificationSnapshotKey(),
    );

    const jobUid = this.route.snapshot.paramMap.get('jobUid');
    if (!jobUid) {
      this.errorMessage.set('Unable to open this job. Missing job ID.');
      this.isLoading.set(false);
      this.isFilesLoading.set(false);
      return;
    }
    this.loadOrder(jobUid);
  }

  ngAfterViewInit(): void {
    this.syncTabIndicator();
  }

  ngOnDestroy(): void {
    this.clearTabTransitionTimers();
    if (this.hasWindow) {
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.toolbarTitleService.clearContextualTitle();
  }

  setActiveTab(tab: JobDetailTabId): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.syncTabIndicator();

    const jobUid = this.route.snapshot.paramMap.get('jobUid') ?? '';

    if (
      tab === 'quotes' &&
      this.notificationService.isAwaitingVendorJob(jobUid)
    ) {
      this.notificationService.acknowledgeVendorJob(jobUid);
    }

    if (
      tab === 'translated-files' &&
      this.notificationService.isCompletedDocsJob(jobUid)
    ) {
      this.notificationService.acknowledgeCompletedJob(jobUid);
    }

    if (tab === 'messages' && this.hasNewMessage()) {
      this.hasNewMessage.set(false);
      this.notificationService.decrementNewMessageNotification();
      if (this.hasWindow) {
        const { newMessage: _, ...rest } = (history.state ?? {}) as Record<
          string,
          unknown
        >;
        history.replaceState(rest, '');
      }
    }

    this.clearTabTransitionTimers();
    this.tabContentState.set('exiting');

    this.fadeExitTimerId = setTimeout(() => {
      this.renderedTab.set(tab);
      this.tabContentState.set('entering');

      this.fadeEnterTimerId = setTimeout(() => {
        this.tabContentState.set('idle');
        this.fadeEnterTimerId = null;
      }, JobDetailComponent.TAB_FADE_DURATION_MS);

      this.fadeExitTimerId = null;
    }, JobDetailComponent.TAB_FADE_DURATION_MS);
  }

  onTabsKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }

    event.preventDefault();

    const currentIndex = this.tabs().findIndex(
      (tab) => tab.id === this.activeTab(),
    );
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex =
      (currentIndex + delta + this.tabs().length) % this.tabs().length;
    const nextTab = this.tabs()[nextIndex];

    this.setActiveTab(nextTab.id);
    this.tabButtons()[nextIndex]?.nativeElement.focus();
  }

  getTabAriaControls(tabId: JobDetailTabId): string {
    return `job-detail-tab-panel-${tabId}`;
  }

  getTabId(tabId: JobDetailTabId): string {
    return `job-detail-tab-${tabId}`;
  }

  getRenderedTabPanelLabel(): string {
    return (
      this.tabs().find((tab) => tab.id === this.renderedTab())?.label ?? ''
    );
  }

  handleWindowResize = (): void => {
    this.syncTabIndicator();
  };

  private clearTabTransitionTimers(): void {
    if (this.fadeExitTimerId) {
      clearTimeout(this.fadeExitTimerId);
      this.fadeExitTimerId = null;
    }

    if (this.fadeEnterTimerId) {
      clearTimeout(this.fadeEnterTimerId);
      this.fadeEnterTimerId = null;
    }
  }

  private syncTabIndicator(): void {
    requestAnimationFrame(() => {
      const navElement = this.tabsNav()?.nativeElement;
      const activeIndex = this.tabs().findIndex(
        (tab) => tab.id === this.activeTab(),
      );
      const activeButtonElement = this.tabButtons()[activeIndex]?.nativeElement;

      if (!navElement || !activeButtonElement) {
        this.tabIndicatorWidth.set(0);
        this.tabIndicatorOffset.set(0);
        return;
      }

      const navRect = navElement.getBoundingClientRect();
      const activeRect = activeButtonElement.getBoundingClientRect();

      this.tabIndicatorOffset.set(activeRect.left - navRect.left);
      this.tabIndicatorWidth.set(activeRect.width);
    });
  }

  constructor() {
    if (this.hasWindow) {
      window.addEventListener('resize', this.handleWindowResize);
    }

    effect(() => {
      this.tabButtons();
      this.activeTab();
      this.syncTabIndicator();
    });
  }

  toggleFileSort(table: FileTableType, column: FileSortColumn): void {
    this.getSortSignal(table).update((state) => {
      if (state.column === column) {
        return { ...state, descending: !state.descending };
      }

      return {
        column,
        descending: false,
      };
    });
  }

  getFileAriaSort(
    table: FileTableType,
    column: FileSortColumn,
  ): 'ascending' | 'descending' | null {
    const state = this.getSortSignal(table)();

    if (state.column !== column) {
      return null;
    }

    return state.descending ? 'descending' : 'ascending';
  }

  navigateBack(): void {
    this.router.navigate(['/translation-manager'], {
      state: {
        fromDetail: true,
        selectedFilters: this.selectedFilters(),
        notificationSnapshot: this.notificationSnapshotOnEntry(),
      },
    });
  }

  downloadSourceFile(fileName: string): void {
    void this.downloadFile(fileName, TranslationFileType.SOURCE);
  }

  downloadReferenceFile(fileName: string): void {
    void this.downloadFile(fileName, TranslationFileType.REFERENCE);
  }

  onQuoteConfirmed(jobUid: string): void {
    this.loadOrder(jobUid, true);
  }

  private async cancelJob(jobUid: string): Promise<void> {
    try {
      await firstValueFrom(this.vendorService.cancelVendorOrder(jobUid));
      this.loadOrder(jobUid, true);
      this.popupService.open({
        title: 'Cancel Job',
        component: CancelJobSuccessContentComponent,
        hideButtons: true,
        dialogTestId: 'cancel-job-success-message',
      });
    } catch (err: unknown) {
      this.loadOrder(jobUid, true);
      this.popupService.open({
        title: 'Unable to cancel job',
        message: this.vendorService.handleErrors(err as never),
        hideButtons: true,
      });
    }
  }

  onJobCompleted(): void {
    const jobUid = this.route.snapshot.paramMap.get('jobUid');
    if (jobUid) {
      this.loadOrder(jobUid, true);
    }
  }

  private loadOrder(jobUid: string, _forceRefresh = false): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.sourceFiles.set([]);
    this.referenceFiles.set([]);

    this.vendorOrdersService
      .getJobDetail(jobUid, this.vendorName, this.rawJobStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (order) => {
          if (order) {
            this.order.set(order);
            this.toolbarTitleService.setContextualTitle(
              this.buildRequestToolbarTitle(order.jobNumber, order.jobUid),
            );
            void this.loadFolderFiles(jobUid, order.isDtpJob);
          } else {
            this.errorMessage.set('Translation job not found.');
            this.isFilesLoading.set(false);
            this.toolbarTitleService.clearContextualTitle();
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.isFilesLoading.set(false);
          this.toolbarTitleService.clearContextualTitle();
          this.errorMessage.set(
            'Unable to load job details right now. Please try again.',
          );
        },
      });
  }

  private mapOrderToView(order: VendorOrder): JobDetailView {
    const sourceFiles = this.sourceFiles();
    const referenceFiles = this.referenceFiles();
    const isDtpJob = order.isDtpJob === true;
    const useRevised = this.shouldUseRevisedClassification(
      order.jobStatus,
      order.vendor,
    );
    const resolvedFiles = this.resolveClassificationFiles(
      order.files,
      useRevised,
    );
    const { basicFormatFiles, dtpFiles } =
      this.splitSourceFilesByClassification(
        sourceFiles,
        resolvedFiles,
        isDtpJob,
      );

    const contentType =
      order.contentType ?? order.files?.[0]?.contentType ?? '--';

    const sourceLocaleId =
      order.sourceLocaleId ??
      order.files?.find((f) => f.sourceLocaleId)?.sourceLocaleId;
    const targetLocaleIds: string[] = order.targetLocaleId?.length
      ? order.targetLocaleId
      : [
          ...new Set(
            (order.files ?? [])
              .map((f) => f.localeId)
              .filter((l): l is string => Boolean(l)),
          ),
        ];
    const statusLabel = this.getStatusLabel(order.jobStatus);

    return {
      jobUid: order.jobUid ?? '--',
      jobNumber: order.jobNumber ?? '--',
      jobName: order.customJobName ?? '--',
      requestDate: this.formatDateValue(order.createdAt),
      targetDate: this.formatDateValue(this.getTargetDate(order)),
      statusLabel,
      statusKey: this.getStatusKey(statusLabel),
      statusIcon: this.getStatusIcon(statusLabel),
      contentType: contentType,
      therapeuticArea: order.therapeuticArea ?? '--',
      costCentre: order.costCentre ?? '--',
      targetAudience: order.targetAudience ?? '--',
      translationCertificate: order.translationCertificateRequired
        ? 'Yes'
        : 'No',
      documentTone: order.documentTone ?? '--',
      additionalInfo: order.additionalInfo || '',
      cancellationReason: order.cancellationReason?.trim() ?? '',
      cancellationDate: order.cancellationDate
        ? this.formatDateValue(order.cancellationDate)
        : undefined,
      sourceLanguage: this.getLocaleName(sourceLocaleId),
      targetLanguages: this.getTargetLanguages(targetLocaleIds),
      basicFormatFiles: this.sortFiles(basicFormatFiles, this.basicFilesSort()),
      dtpFiles: this.sortFiles(dtpFiles, this.dtpFilesSort()),
      referenceFiles: this.sortFiles(referenceFiles, this.referenceFilesSort()),
    };
  }

  private shouldUseRevisedClassification(
    jobStatus: string | undefined,
    vendor: { name: string; id: string } | undefined,
  ): boolean {
    if (!jobStatus || !QUALIFYING_STATUSES_FOR_REVISED_DTP.has(jobStatus)) {
      return false;
    }
    if (jobStatus === 'CANCELLED' && !vendor?.id) {
      return false;
    }
    return true;
  }

  private resolveClassificationFiles(
    files: VendorOrderFile[] | undefined,
    useRevised: boolean,
  ): VendorOrderFile[] {
    if (!files) {
      return [];
    }
    if (!useRevised) {
      return files;
    }
    return files.map((file) =>
      typeof file.isDtpFileRevised === 'boolean'
        ? { ...file, isDtpFile: file.isDtpFileRevised }
        : file,
    );
  }

  private splitSourceFilesByClassification(
    sourceFiles: SourceFile[],
    orderFiles: VendorOrderFile[] | undefined,
    isDtpJob: boolean,
  ): { basicFormatFiles: SourceFile[]; dtpFiles: SourceFile[] } {
    const classifiedOrderFiles = (orderFiles ?? []).filter(
      (file): file is VendorOrderFile & { name: string; isDtpFile: boolean } =>
        typeof file?.name === 'string' &&
        file.name.trim().length > 0 &&
        typeof file.isDtpFile === 'boolean',
    );

    if (classifiedOrderFiles.length === 0) {
      return isDtpJob
        ? { basicFormatFiles: [], dtpFiles: [...sourceFiles] }
        : { basicFormatFiles: [...sourceFiles], dtpFiles: [] };
    }

    const dtpCounts = new Map<string, number>();
    const nonDtpCounts = new Map<string, number>();

    for (const file of classifiedOrderFiles) {
      const key = this.normalizeFileKey(file.name);
      const targetMap = file.isDtpFile ? dtpCounts : nonDtpCounts;
      targetMap.set(key, (targetMap.get(key) ?? 0) + 1);
    }

    const basicFormatFiles: SourceFile[] = [];
    const dtpFiles: SourceFile[] = [];

    for (const sourceFile of sourceFiles) {
      const key = this.normalizeFileKey(sourceFile.name);
      const remainingDtp = dtpCounts.get(key) ?? 0;
      const remainingNonDtp = nonDtpCounts.get(key) ?? 0;

      if (remainingDtp > 0) {
        dtpFiles.push(sourceFile);
        dtpCounts.set(key, remainingDtp - 1);
        continue;
      }

      if (remainingNonDtp > 0) {
        basicFormatFiles.push(sourceFile);
        nonDtpCounts.set(key, remainingNonDtp - 1);
        continue;
      }

      if (isDtpJob) {
        dtpFiles.push(sourceFile);
      } else {
        basicFormatFiles.push(sourceFile);
      }
    }

    return {
      basicFormatFiles,
      dtpFiles,
    };
  }

  private normalizeFileKey(name: string): string {
    return name.trim().toLowerCase();
  }

  private async downloadFile(
    fileName: string,
    fileType: TranslationFileType,
  ): Promise<void> {
    const jobUid = this.order()?.jobUid;
    if (!jobUid) {
      return;
    }

    try {
      const { url } = await this.s3FilesService.getPresignedUrlForFile(
        fileName,
        S3DestinationFolder.TRANSLATION,
        true,
        jobUid,
        fileType,
      );

      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fileName;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    } catch {
      // Ignore download errors; users can retry.
    }
  }

  private async loadFolderFiles(
    jobUid: string,
    isDtpJob?: boolean,
  ): Promise<void> {
    if (!jobUid) {
      this.sourceFiles.set([]);
      this.referenceFiles.set([]);
      this.isFilesLoading.set(false);
      return;
    }

    this.isFilesLoading.set(true);

    try {
      const [sourceResponse, referenceResponse] = await Promise.all([
        this.s3FilesService.getFilesInFolderWithSize(
          TranslationFileType.SOURCE,
          jobUid,
        ),
        this.s3FilesService.getFilesInFolderWithSize(
          TranslationFileType.REFERENCE,
          jobUid,
        ),
      ]);

      const mappedSourceFiles = (sourceResponse?.files ?? []).map((file) => ({
        name: file.name,
        size: this.formatBytes(file.size),
        bytes: file.size,
      }));

      const mappedReferenceFiles = (referenceResponse?.files ?? []).map(
        (file) => ({
          name: file.name,
          size: this.formatBytes(file.size),
          bytes: file.size,
        }),
      );

      const filteredReferenceFiles = this.filterReferenceDuplicates(
        mappedSourceFiles,
        mappedReferenceFiles,
      );

      this.sourceFiles.set(mappedSourceFiles);
      this.referenceFiles.set(filteredReferenceFiles);
    } catch {
      this.sourceFiles.set([]);
      this.referenceFiles.set([]);
    } finally {
      this.isFilesLoading.set(false);
    }
  }

  private buildRequestToolbarTitle(
    jobNumber?: string,
    fallbackUid?: string,
  ): string | null {
    const cleanedJobNumber = jobNumber?.replace(/^AZT-/i, '').trim() ?? '';

    if (cleanedJobNumber.length > 0) {
      return `Request ${cleanedJobNumber}`;
    }

    const cleanedFallbackUid = fallbackUid?.trim() ?? '';
    if (cleanedFallbackUid.length > 0) {
      return `Request ${cleanedFallbackUid}`;
    }

    return null;
  }

  private getSelectedFiltersFromState(): string[] {
    const state = history.state as { selectedFilters?: string[] } | null;
    if (!Array.isArray(state?.selectedFilters)) {
      return [];
    }

    return state.selectedFilters.filter(
      (value) => typeof value === 'string' && value.trim().length > 0,
    );
  }

  private formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes)) {
      return '--';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }

    return `${this.formatDecimal(size)} ${units[unitIndex]}`;
  }

  private formatDecimal(value: number): string {
    if (Number.isInteger(value)) {
      return value.toString();
    }

    const trimmed = value
      .toFixed(2)
      .replace(/\.0+$/, '')
      .replace(/(\.\d*?)0+$/, '$1');
    return trimmed;
  }

  private filterReferenceDuplicates(
    sourceFiles: SourceFile[],
    referenceFiles: SourceFile[],
  ): SourceFile[] {
    const sourceCounts = new Map<string, number>();
    for (const file of sourceFiles) {
      const key = file.name.toLowerCase();
      sourceCounts.set(key, (sourceCounts.get(key) ?? 0) + 1);
    }

    const filtered: SourceFile[] = [];
    for (const file of referenceFiles) {
      const key = file.name.toLowerCase();
      const remaining = sourceCounts.get(key) ?? 0;
      if (remaining > 0) {
        sourceCounts.set(key, remaining - 1);
        continue;
      }
      filtered.push(file);
    }

    return filtered;
  }

  private sortFiles(
    files: SourceFile[],
    sortState: FileSortState,
  ): SourceFile[] {
    const direction = sortState.descending ? -1 : 1;

    return [...files].sort((left, right) => {
      if (sortState.column === 'name') {
        return (
          left.name.localeCompare(right.name, undefined, {
            sensitivity: 'base',
          }) * direction
        );
      }

      return (left.bytes - right.bytes) * direction;
    });
  }

  private getSortSignal(table: FileTableType) {
    switch (table) {
      case 'basic':
        return this.basicFilesSort;
      case 'dtp':
        return this.dtpFilesSort;
      default:
        return this.referenceFilesSort;
    }
  }

  private getTargetDate(order: VendorOrder): string | undefined {
    const estimatedDate = order.estimatedDeliveryDate;
    const fixedDate = order.fixedDeliveryDate;
    let targetDate = order.deadline;

    if (estimatedDate) {
      targetDate = estimatedDate;
    }

    if (fixedDate && fixedDate !== estimatedDate) {
      targetDate = fixedDate;
    }

    return targetDate;
  }

  private formatDateValue(value?: number | string): string {
    if (value === undefined || value === null) {
      return '--';
    }

    const dateValue =
      typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return '--';
    }

    return formatDate(dateValue, 'dd/MMM/yyyy', 'en-GB');
  }

  private getLocaleName(localeId?: string): string {
    if (!localeId) {
      return '--';
    }
    return LOCALE_LOOKUP.get(localeId) ?? localeId;
  }

  private getTargetLanguages(localeIds?: string[]): string[] {
    if (!localeIds || localeIds.length === 0) {
      return [];
    }
    return localeIds.map((id) => this.getLocaleName(id));
  }

  private getStatusLabel(status?: string): string {
    if (!status) {
      return '--';
    }
    const mapped =
      JOB_STATUSES_NEW_TAGS[status as keyof typeof JOB_STATUSES_NEW_TAGS];
    return mapped ?? this.formatFallbackStatus(status);
  }

  private formatFallbackStatus(status: string): string {
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  private getStatusKey(statusLabel?: string): string {
    if (!statusLabel || statusLabel === '--') {
      return 'unknown';
    }
    return statusLabel
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  private getStatusIcon(statusLabel?: string): string | null {
    if (!statusLabel || statusLabel === '--') {
      return null;
    }
    return STATUS_ICON_MAP[statusLabel] ?? null;
  }

  cancelledReasonLabel(): string {
    const reason = this.jobDetail()?.cancellationReason?.trim() ?? '';

    if (reason.length === 0) {
      return 'No cancellation reason provided.';
    }

    return CANCELLATION_REASON_DISPLAY_MAP[reason] ?? reason;
  }
}
